import { Grid, Group, Layout, Page } from "@/comn/components";
import { useNavigate } from "react-router-dom";
import { comnUtils } from "@/comn/utils";
import { useFetch, useForm, useGrid, useModal, usePage, useResource, useTheme, useToast } from "@/comn/hooks";
import {
    APIS,
    SF_ROLE_MGMT_DTL,
    SG_ROLE_LANG_LIST,
    URLS,
} from "@/tz/go/egaz/osci/ptl/ptl/role/services/OsciRoleService";
import { useTranslation } from "react-i18next";
import React, { useEffect } from "react";
import { OSCI0104019Q } from "@/tz/go/egaz/osci/ptl/ptl/role/OSCI0104019Q";
import { SG_ROLE_AUTH_CNCT_LIST } from "@/tz/go/egaz/osci/ptl/ptl/auth/services/OsciRoleAuthService";
import { OSCI0104018Q } from "@/tz/go/egaz/osci/ptl/ptl/role/OSCI0104018Q";

/*
 * Register for Role Management
 * !== 역할 관리 등록 ==!
 */
export const OSCI0104016S = (): JSX.Element => {
    const {} = usePage({
        auth: ["auth_osci0003"],
    });
    const navigate = useNavigate();
    const modal = useModal();
    const toast = useToast();
    const { theme } = useTheme();
    const { t } = useTranslation();
    const langCodes = ["KO", "EN", "TZ"];

    useResource({ defaultSchema: [{ area: "comnCd", comnCd: "OSC0095" }] });

    const form = { roleDtl: useForm({ defaultSchema: SF_ROLE_MGMT_DTL }) };

    const grid = {
        roleLangList: useGrid({ defaultSchema: SG_ROLE_LANG_LIST }),
        roleAuthList: useGrid({ defaultSchema: SG_ROLE_AUTH_CNCT_LIST }),
    };

    const fetch = {
        createRoleDtl: useFetch({
            api: () =>
                APIS.createRoleDtl({
                    ...form.roleDtl.getValues(),
                    roleLangList: grid.roleLangList.getData(),
                    cnctList: grid.roleAuthList.getData(),
                }),
            enabled: false,
            onSuccess: () => {
                modal.openModal({
                    content: "msg.osc.00069",
                    onClose: () => handler.onListBtnClick(),
                }); // Successfully inserted. !== 정상적으로 등록되었습니다. ==!
            },
            onError: () => toast.showToast({ type: "error", content: "msg.osc.00035" }), // An error occurred. !== 에러가 발생했습니다! ==!
        }),
    };

    const render = {
        roleAuthList: {
            cell: {
                authRelaMenuCnt: (args: any) => {
                    const { rowValues, value } = args;
                    return (
                        <button
                            className="underline"
                            onClick={() => {
                                modal.openModal({
                                    draggable: true,
                                    size: "sm",
                                    content: (
                                        <OSCI0104019Q
                                            ptlTpCd={form.roleDtl.getValues("ptlTpCd")}
                                            authId={rowValues.authId}
                                        />
                                    ),
                                });
                            }}
                        >
                            {value}
                        </button>
                    );
                },
            },
        },
    };

    const handler = {
        onGridAddBtnClick: () => {
            const modalCallback = (list: any[]) => {
                !comnUtils.isEmpty(list) && list.forEach((item: any) => grid.roleAuthList.addRow({ ...item }));
                modal.closeModal();
            };

            modal.openModal({
                draggable: true,
                size: "xl",
                content: (
                    <OSCI0104018Q
                        roleId={"new"}
                        ptlTpCd={form.roleDtl.getValue("ptlTpCd")}
                        callback={modalCallback}
                        roleAuthList={grid.roleAuthList.getData()}
                    />
                ),
            });
        },
        onGridDelBtnClick: () => {
            if (!grid.roleAuthList.isChecked()) {
                modal.openModal({ content: "msg.osc.00193" }); // Please select a destination to delete. !== 삭제대상을 선택하세요. ==!
                return;
            }
            modal.openModal({
                content: "msg.osc.00062",
                onConfirm: () => {
                    grid.roleAuthList.deleteRow("checkbox");
                },
            });
        },
        onListBtnClick: () => navigate(`${URLS.osci0104015q}`),
        onSubmitBtnClick: () => {
            form.roleDtl.handleSubmit(
                () => {
                    modal.openModal({
                        content: "msg.osc.00090", // Do you want to save ? !== 저장 하시겠습니까 ? ==!
                        onConfirm: () => {
                            grid.roleLangList.clearData();

                            langCodes.forEach((langCd: any) => {
                                const roleNm = form.roleDtl.getValues(`roleNm${langCd}`);
                                const roleDesc = form.roleDtl.getValues(`roleDesc${langCd}`);

                                if (!comnUtils.isEmpty(roleNm) || !comnUtils.isEmpty(roleDesc)) {
                                    grid.roleLangList.addRow({ langCd: langCd, roleNm: roleNm, roleDesc: roleDesc });
                                }
                            });

                            fetch.createRoleDtl.fetch();
                        },
                    });
                },
                () => toast.showToast({ type: "warning", content: "msg.osc.00003" }), // Data is invalid, !== 데이터가 유효하지않습니다. ==!
            )();
        },
    };

    useEffect(() => {
        form.roleDtl.setValue("roleId", t("msg.osc.00207"));
    }, [theme.lang]);

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.roleDtl.schema.roleId} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.roleDtl.schema.roleNmTZ} />
                            <Group.Control {...form.roleDtl.schema.roleDescTZ} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.roleDtl.schema.roleNmEN} />
                            <Group.Control {...form.roleDtl.schema.roleDescEN} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.roleDtl.schema.roleNmKO} />
                            <Group.Control {...form.roleDtl.schema.roleDescKO} />
                        </Group.Row>
                    </Group.Section>

                    <Group.Section hidden={true}>
                        <Grid {...grid.roleLangList.grid} />
                    </Group.Section>
                </Group.Body>
            </Group>

            <Group>
                <Group.Body>
                    <Group.Section>
                        <Layout>
                            <Layout.Right>
                                <Layout.Button role={"gridAdd"} onClick={handler.onGridAddBtnClick} />
                                <Layout.Button role={"gridDelete"} onClick={handler.onGridDelBtnClick} />
                            </Layout.Right>
                        </Layout>
                    </Group.Section>
                    <Group.Section>
                        <Grid {...grid.roleAuthList.grid} render={render.roleAuthList} />
                    </Group.Section>
                </Group.Body>
            </Group>

            <Layout>
                <Layout.Left>
                    <Layout.Button role={"list"} onClick={() => handler.onListBtnClick()} />
                </Layout.Left>
                <Layout.Right>
                    <Layout.Button role={"save"} onClick={handler.onSubmitBtnClick} />
                </Layout.Right>
            </Layout>
        </Page>
    );
};
